import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getFDPaymentTypes, getBankDetails, getFDNumbers, getFDDetailsbyFdno,
    getFDBudgetClosingFYYear, getFDLastTransaction,
    getAllDCA, getSubDCAbyDCA, getFDIntDeductionData,
    saveOpenFD, saveParitalFD, saveFDInterest,
} from '../../api/AccountsAPI/fdPaymentsAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchFDPaymentTypes = createAsyncThunk('fdPayments/fetchFDPaymentTypes', async (_, { rejectWithValue }) => {
    try { const res = await getFDPaymentTypes(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch transaction types')); }
});

export const fetchFDBankList = createAsyncThunk('fdPayments/fetchBankList', async (_, { rejectWithValue }) => {
    try { const res = await getBankDetails(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch bank list')); }
});

export const fetchFDNumbers = createAsyncThunk('fdPayments/fetchFDNumbers', async (_, { rejectWithValue }) => {
    try { const res = await getFDNumbers(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch FD numbers')); }
});

export const fetchFDDetailsByFdno = createAsyncThunk('fdPayments/fetchFDDetails', async (fdno, { rejectWithValue }) => {
    try { const res = await getFDDetailsbyFdno(fdno); return res?.Data?.[0] || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch FD details')); }
});

// Return Budget FY Year options for FD Close/Partial Close — required as of the 2026-09
// spPartialFD update (its own budget-existence check fails the whole submission without it).
export const fetchReturnBudgetFYYears = createAsyncThunk('fdPayments/fetchReturnBudgetFYYears', async (fdno, { rejectWithValue }) => {
    try { const res = await getFDBudgetClosingFYYear(fdno); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch budget return FY years')); }
});

// Most recent approved bank transaction for this FD — its PaymentBankName is what populates the
// read-only Bank field on Close/Partial Close (GetFDDetailsbyFdno never returns bank info).
export const fetchFDLastTransaction = createAsyncThunk('fdPayments/fetchLastTransaction', async (fdno, { rejectWithValue }) => {
    try { const res = await getFDLastTransaction(fdno); return res?.Data?.[0] || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch last FD transaction details')); }
});

export const fetchFDDCAList = createAsyncThunk('fdPayments/fetchDCAList', async (_, { rejectWithValue }) => {
    try { const res = await getAllDCA(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch account heads')); }
});

export const fetchFDSubDCAByDCACode = createAsyncThunk('fdPayments/fetchSubDCA', async (dcaCode, { rejectWithValue }) => {
    try { const res = await getSubDCAbyDCA(dcaCode); return { dcaCode, list: res?.Data || [] }; }
    catch (e) { return rejectWithValue({ dcaCode, message: err(e, 'Failed to fetch sub account heads') }); }
});

export const fetchFDIntDeductionDefault = createAsyncThunk('fdPayments/fetchIntDeductionDefault', async (_, { rejectWithValue }) => {
    try { const res = await getFDIntDeductionData(); return res?.Data || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch default deduction account head')); }
});

export const submitOpenFD = createAsyncThunk('fdPayments/submitOpenFD', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveOpenFD(payload);
        const status = res?.Data;
        if (status !== 'Submited' && status !== 'Submitted') return rejectWithValue(status || 'Failed to save FD');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save FD')); }
});

export const submitPartialFD = createAsyncThunk('fdPayments/submitPartialFD', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveParitalFD(payload);
        const status = res?.Data;
        if (status !== 'Submited' && status !== 'Submitted') return rejectWithValue(status || 'Failed to save transaction');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save transaction')); }
});

export const submitFDInterest = createAsyncThunk('fdPayments/submitFDInterest', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveFDInterest(payload);
        const status = res?.Data;
        if (status !== 'Submited' && status !== 'Submitted') return rejectWithValue(status || 'Failed to save interest');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save interest')); }
});

const initialState = {
    fdPaymentTypes: [], fdPaymentTypesLoading: false,
    bankList: [], bankListLoading: false,
    fdNumbers: [], fdNumbersLoading: false,
    fdDetails: null, fdDetailsLoading: false,

    lastTransaction: null, lastTransactionLoading: false,

    returnBudgetFYYears: [], returnBudgetFYYearsLoading: false,

    dcaList: [], dcaListLoading: false,
    subDcaByDca: {}, subDcaLoadingByDca: {},

    intDeductionDefault: null, intDeductionDefaultLoading: false,

    saveLoading: false, saveResult: null, saveError: null,
};

const fdPaymentsSlice = createSlice({
    name: 'fdPayments',
    initialState,
    reducers: {
        clearFDDetails: (s) => { s.fdDetails = null; s.returnBudgetFYYears = []; s.lastTransaction = null; },
        clearFDSaveResult: (s) => { s.saveResult = null; s.saveError = null; },
        resetFDPayments: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFDPaymentTypes.pending, (s) => { s.fdPaymentTypesLoading = true; })
            .addCase(fetchFDPaymentTypes.fulfilled, (s, a) => { s.fdPaymentTypesLoading = false; s.fdPaymentTypes = a.payload; })
            .addCase(fetchFDPaymentTypes.rejected, (s) => { s.fdPaymentTypesLoading = false; });

        builder
            .addCase(fetchFDBankList.pending, (s) => { s.bankListLoading = true; })
            .addCase(fetchFDBankList.fulfilled, (s, a) => { s.bankListLoading = false; s.bankList = a.payload; })
            .addCase(fetchFDBankList.rejected, (s) => { s.bankListLoading = false; });

        builder
            .addCase(fetchFDNumbers.pending, (s) => { s.fdNumbersLoading = true; })
            .addCase(fetchFDNumbers.fulfilled, (s, a) => { s.fdNumbersLoading = false; s.fdNumbers = a.payload; })
            .addCase(fetchFDNumbers.rejected, (s) => { s.fdNumbersLoading = false; });

        builder
            .addCase(fetchFDDetailsByFdno.pending, (s) => { s.fdDetailsLoading = true; s.fdDetails = null; })
            .addCase(fetchFDDetailsByFdno.fulfilled, (s, a) => { s.fdDetailsLoading = false; s.fdDetails = a.payload; })
            .addCase(fetchFDDetailsByFdno.rejected, (s) => { s.fdDetailsLoading = false; });

        builder
            .addCase(fetchFDLastTransaction.pending, (s) => { s.lastTransactionLoading = true; s.lastTransaction = null; })
            .addCase(fetchFDLastTransaction.fulfilled, (s, a) => { s.lastTransactionLoading = false; s.lastTransaction = a.payload; })
            .addCase(fetchFDLastTransaction.rejected, (s) => { s.lastTransactionLoading = false; });

        builder
            .addCase(fetchReturnBudgetFYYears.pending, (s) => { s.returnBudgetFYYearsLoading = true; s.returnBudgetFYYears = []; })
            .addCase(fetchReturnBudgetFYYears.fulfilled, (s, a) => { s.returnBudgetFYYearsLoading = false; s.returnBudgetFYYears = a.payload; })
            .addCase(fetchReturnBudgetFYYears.rejected, (s) => { s.returnBudgetFYYearsLoading = false; });

        builder
            .addCase(fetchFDDCAList.pending, (s) => { s.dcaListLoading = true; })
            .addCase(fetchFDDCAList.fulfilled, (s, a) => { s.dcaListLoading = false; s.dcaList = a.payload; })
            .addCase(fetchFDDCAList.rejected, (s) => { s.dcaListLoading = false; });

        builder
            .addCase(fetchFDSubDCAByDCACode.pending, (s, a) => { s.subDcaLoadingByDca[a.meta.arg] = true; })
            .addCase(fetchFDSubDCAByDCACode.fulfilled, (s, a) => { s.subDcaLoadingByDca[a.payload.dcaCode] = false; s.subDcaByDca[a.payload.dcaCode] = a.payload.list; })
            .addCase(fetchFDSubDCAByDCACode.rejected, (s, a) => { s.subDcaLoadingByDca[a.meta.arg] = false; });

        builder
            .addCase(fetchFDIntDeductionDefault.pending, (s) => { s.intDeductionDefaultLoading = true; })
            .addCase(fetchFDIntDeductionDefault.fulfilled, (s, a) => { s.intDeductionDefaultLoading = false; s.intDeductionDefault = a.payload; })
            .addCase(fetchFDIntDeductionDefault.rejected, (s) => { s.intDeductionDefaultLoading = false; });

        [submitOpenFD, submitPartialFD, submitFDInterest].forEach((thunk) => {
            builder
                .addCase(thunk.pending, (s) => { s.saveLoading = true; s.saveError = null; s.saveResult = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.saveLoading = false; s.saveResult = a.payload; })
                .addCase(thunk.rejected, (s, a) => { s.saveLoading = false; s.saveError = a.payload; });
        });
    },
});

export const { clearFDDetails, clearFDSaveResult, resetFDPayments } = fdPaymentsSlice.actions;

export const selectFDPayments = (s) => s.fdPayments;

export default fdPaymentsSlice.reducer;
